import { FC } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import PostTypeIcon from 'app/modules/posts/components/PostTypeIcon';
import MetricNotAvailable from 'app/modules/analytics/components/shared/MetricNotAvailable';
import { NOT_AVAILABLE } from 'app/modules/analytics/modules/performance/utils/getPerformanceMetricDisplay';
import type { PseudoPostType } from 'constants/post';
import type {
  ContentPostCellHighlight,
  ContentPostTableRow,
} from '../types/contentTableRow';
import type { ContentTableTotal } from '../utils/getContentTableTotals';

const badgeHighlightClass: Record<ContentPostCellHighlight, string> = {
  positive:
    'tw-inline-flex tw-items-center tw-gap-1 tw-rounded-lg tw-bg-success-10 tw-px-1 tw-py-1 tw-font-medium tw-text-success-120',
  negative:
    'tw-inline-flex tw-items-center tw-gap-1 tw-rounded-lg tw-bg-danger-10 tw-px-1 tw-py-1 tw-font-medium tw-text-danger-120',
};

interface MetricCellValueProps {
  value: string;
  highlight?: ContentPostCellHighlight;
}

export const MetricCellValue: FC<MetricCellValueProps> = ({
  value,
  highlight,
}) => {
  if (value === NOT_AVAILABLE) {
    return (
      <span className="tw-font-medium tw-text-grayscale-180">
        <MetricNotAvailable />
      </span>
    );
  }
  if (!highlight) {
    return (
      <span className="tw-font-medium tw-text-grayscale-180">{value}</span>
    );
  }
  return <span className={badgeHighlightClass[highlight]}>{value}</span>;
};

interface MetricTotalValueProps {
  total: ContentTableTotal;
}

export const MetricTotalValue: FC<MetricTotalValueProps> = ({ total }) => (
  <span className="tw-font-semibold tw-text-grayscale-180">
    {total.aggregation === 'average'
      ? `\u00d8 ${total.display}`
      : total.display}
  </span>
);

interface PostTypeCellProps {
  type: PseudoPostType;
}

export const PostTypeCell: FC<PostTypeCellProps> = ({ type }) => (
  <div className="tw-flex tw-h-6 tw-w-7 tw-items-center tw-justify-center tw-rounded tw-bg-grayscale-5">
    <PostTypeIcon type={type} size="sm" />
  </div>
);

interface ContentPreviewCellProps {
  row: ContentPostTableRow;
  size: 'comfortable' | 'compact';
}

export const ContentPreviewCell: FC<ContentPreviewCellProps> = ({
  row,
  size,
}) => {
  const { t } = useTranslation();
  const isCompact = size === 'compact';

  return (
    <div className="tw-flex tw-items-center tw-gap-3">
      <div
        className={clsx(
          'tw-relative tw-shrink-0 tw-overflow-hidden tw-rounded-lg tw-bg-grayscale-10',
          isCompact ? 'tw-h-9 tw-w-9' : 'tw-h-12 tw-w-12',
        )}
      >
        {row.previewImageUrl ? (
          <>
            <img
              src={row.previewImageUrl}
              alt=""
              className="tw-h-full tw-w-full tw-object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.removeAttribute('hidden');
              }}
            />
            <div
              hidden
              className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center"
            >
              <PostTypeIcon
                type={row.postType}
                className="tw-text-grayscale-50"
              />
            </div>
          </>
        ) : (
          <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center">
            <PostTypeIcon
              type={row.postType}
              className="tw-text-grayscale-50"
            />
          </div>
        )}
      </div>
      <p
        className={clsx(
          'tw-font-normal tw-line-clamp-2 tw-min-w-0',
          isCompact ? 'tw-text-xs' : 'tw-max-w-[160px] tw-text-sm',
          row.previewText
            ? 'tw-text-grayscale-180'
            : 'tw-italic tw-text-grayscale-100',
        )}
      >
        {row.previewText
          ? row.previewText
          : t('analyticsContent.noCaptionPlaceholder')}
      </p>
    </div>
  );
};
